import React, { useState, useEffect } from 'react';
import Select from '../../../components/ui/Select';
import Pagination, { usePagination } from '../../../components/ui/Pagination';
import EmptyState from '../../../components/ui/EmptyState';
import { TableSkeleton } from '../../../components/ui/Skeleton';
import SortableTh, { useSort } from '../../../components/ui/SortableTh';
import useTableFilters from '../../../components/ui/useTableFilters';
import Icon from '../../../components/AppIcon';

import Input from '../../../components/ui/Input';
import { adminService } from '../../../services/adminService';

const UserManagement = () => {
  /* State first. Everything below derives from it, and a `const`
     binding from useState is unreachable until its own line runs. */
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Table-local sorting.
  const { sort, toggle, sortRows } = useSort('createdAt', 'desc');
  const f = useTableFilters(users, React.useMemo(() => ({
    institution: { label: 'Institution', accessor: (u) => u?.institution || '', emptyLabel: 'No institution' },
    isActive:    { label: 'Status', accessor: (u) => (u?.isActive ? 'active' : 'inactive'),
      order: ['active','inactive'], format: (v) => (v === 'active' ? 'Active' : 'Inactive') },
  }), []));
  const SORT_ACCESSORS = {
    name: (r) => `${r?.lastName || ''} ${r?.firstName || ''}`.trim() || r?.fullName || r?.email || '',
    isActive: (r) => (r?.isActive ? 1 : 0),
  };
  const sortedRows = sortRows(f.filterRows(users), SORT_ACCESSORS);
  // Paginate after sorting, so page 1 is the top of the sorted list
  // rather than the top of an arbitrary slice.
  const { pageRows, ...pager } = usePagination(sortedRows, 20);

  useEffect(() => {
    loadUsers();
  }, [searchTerm, roleFilter, statusFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const filters = {
        search: searchTerm,
        roles: roleFilter ? [roleFilter] : undefined,
        isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined
      };
      const data = await adminService?.getAllUsers(filters);
      setUsers(data);
    } catch (err) {
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await adminService?.updateUserStatus(userId, !currentStatus);
      loadUsers();
    } catch (err) {
      setError(err?.message || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await adminService?.deleteUser(userId);
      loadUsers();
    } catch (err) {
      setError(err?.message || 'Failed to delete user');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-foreground mb-4">User Management</h2>
        
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            icon="Search"
          />
          <Select
            value={roleFilter}
            onChange={setRoleFilter}
            placeholder="All roles"
            clearable
            className="min-w-[10rem]"
            options={[
              { value: 'author', label: 'Author' },
              { value: 'reviewer', label: 'Reviewer' },
              { value: 'chair', label: 'Chair' },
              { value: 'organizer', label: 'Organizer' },
              { value: 'admin', label: 'Admin' },
            ]}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Any status"
            clearable
            className="min-w-[9rem]"
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />
        </div>

        {error && (
          <div className="mb-4 p-4 bg-error/10 border-2 border-error rounded-lg">
            <p className="text-error text-sm">{error}</p>
          </div>
        )}
      </div>

      {loading ? (
        <TableSkeleton rows={6} columns={5} />
      ) : users?.length === 0 ? (
        <EmptyState
          icon="Users"
          title="No users yet"
          body="Registered accounts will appear here as people sign up."
        />
      ) : (
        <>
        <div className="overflow-x-auto">
          <table className="table-clear">
            <thead>
              <tr>
                <SortableTh field="name" sort={sort} onSort={toggle}>User</SortableTh>
                <SortableTh field="email" sort={sort} onSort={toggle}>Email</SortableTh>
                <SortableTh field="institution" sort={sort} onSort={toggle} filter={f.headerProps('institution')}>Institution</SortableTh>
                <th>Roles</th>
                <SortableTh field="isActive" sort={sort} onSort={toggle} filter={f.headerProps('isActive')}>Status</SortableTh>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows?.map((user) => (
                <tr key={user?.id}>
                  <td>
                    <div className="font-medium text-foreground">
                      {user?.firstName} {user?.lastName}
                    </div>
                  </td>
                  <td className="text-muted-foreground">{user?.email}</td>
                  <td className="text-muted-foreground">{user?.institution}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {user?.roles?.map((role, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`px-2 py-1 text-xs font-medium ${
                        user?.isActive
                          ? 'bg-success/10 text-success' :'bg-error/10 text-error'
                      }`}
                    >
                      {user?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleStatus(user?.id, user?.isActive)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        title={user?.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <Icon
                          name={user?.isActive ? 'UserX' : 'UserCheck'}
                          size={18}
                          className="text-muted-foreground"
                        />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user?.id)}
                        className="p-2 hover:bg-error/10 rounded-lg transition-colors"
                        title="Delete user"
                      >
                        <Icon name="Trash2" size={18} className="text-error" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination {...pager} />
        </>
      )}
    </div>
  );
};

export default UserManagement;